import React, { useState, useEffect } from "react";
import api from '../../lib/axios';
import { toast } from "sonner";
import {
  Building,
  Phone,
  Mail,
  Globe,
  CreditCard,
  FileText,
  Upload,
  Save,
  Image,
  Plus,
  Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CompanySettingsManager = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState({
    company_name: "",
    ruc: "",
    address: "",
    phone: "",
    email: "",
    website: "",
    logo_data: null,
    bank_accounts: [],
    payment_terms: "",
    footer_text: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newBankAccount, setNewBankAccount] = useState({ bank: "", account: "", cci: "" });

  // Fetch settings on open
  useEffect(() => {
    if (isOpen) {
      fetchSettings();
    }
  }, [isOpen]);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`${API}/company-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching company settings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Solo se aceptan archivos de imagen");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("El archivo es muy grande (máx 5MB)");
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`${API}/company-settings/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, logo_data: response.data.logo_data }));
      toast.success("Logo guardado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir logo");
    }
  };

  const addBankAccount = () => {
    if (!newBankAccount.bank || !newBankAccount.account) {
      toast.error("Ingresa banco y número de cuenta");
      return;
    }
    setSettings(prev => ({
      ...prev,
      bank_accounts: [...prev.bank_accounts, { ...newBankAccount }]
    }));
    setNewBankAccount({ bank: "", account: "", cci: "" });
  };

  const removeBankAccount = (index) => {
    setSettings(prev => ({
      ...prev,
      bank_accounts: prev.bank_accounts.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.post(`${API}/company-settings`, settings);
      toast.success("Configuración guardada exitosamente");
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building className="w-5 h-5 text-blue-600" />
            Configuración de Empresa
          </DialogTitle>
          <DialogDescription>
            Personaliza los datos de tu empresa para los PDFs de cotización
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 text-center text-slate-500">Cargando...</div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Logo Section */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Logo de Empresa
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  {settings.logo_data ? (
                    <img 
                      src={settings.logo_data} 
                      alt="Logo" 
                      className="w-24 h-24 object-contain border rounded"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-slate-100 rounded flex items-center justify-center">
                      <Building className="w-8 h-8 text-slate-400" />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="logo-upload" className="cursor-pointer">
                      <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition">
                        <Upload className="w-4 h-4" />
                        Subir Logo
                      </div>
                    </Label>
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                    <p className="text-xs text-slate-500 mt-1">PNG, JPG máx 5MB</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Company Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Datos de Empresa
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company_name">Nombre de Empresa *</Label>
                    <Input
                      id="company_name"
                      value={settings.company_name}
                      onChange={(e) => handleInputChange('company_name', e.target.value)}
                      placeholder="Mi Empresa S.A.C."
                    />
                  </div>
                  <div>
                    <Label htmlFor="ruc">RUC</Label>
                    <Input
                      id="ruc"
                      value={settings.ruc}
                      onChange={(e) => handleInputChange('ruc', e.target.value)}
                      placeholder="20123456789"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="address">Dirección</Label>
                  <Input
                    id="address"
                    value={settings.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Av. Principal 123, Lima"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="phone" className="flex items-center gap-1">
                      <Phone className="w-3 h-3" /> Teléfono
                    </Label>
                    <Input
                      id="phone"
                      value={settings.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="+51 999 999 999"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="flex items-center gap-1">
                      <Mail className="w-3 h-3" /> Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={settings.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="ventas@empresa.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="website" className="flex items-center gap-1">
                      <Globe className="w-3 h-3" /> Web
                    </Label>
                    <Input
                      id="website"
                      value={settings.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      placeholder="www.empresa.com"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bank Accounts */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Cuentas Bancarias
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {settings.bank_accounts.map((account, index) => (
                  <div key={`${account.bank}-${account.account}-${index}`} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                    <div className="flex-1 grid grid-cols-3 gap-2 text-sm">
                      <span className="font-medium">{account.bank}</span>
                      <span>{account.account}</span>
                      <span className="text-slate-500">{account.cci}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBankAccount(index)}
                      className="text-red-500 h-7 w-7 p-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input
                    placeholder="Banco"
                    value={newBankAccount.bank}
                    onChange={(e) => setNewBankAccount(prev => ({ ...prev, bank: e.target.value }))}
                    className="flex-1"
                  />
                  <Input
                    placeholder="N° Cuenta"
                    value={newBankAccount.account}
                    onChange={(e) => setNewBankAccount(prev => ({ ...prev, account: e.target.value }))}
                    className="flex-1"
                  />
                  <Input
                    placeholder="CCI (opcional)"
                    value={newBankAccount.cci}
                    onChange={(e) => setNewBankAccount(prev => ({ ...prev, cci: e.target.value }))}
                    className="flex-1"
                  />
                  <Button onClick={addBankAccount} size="sm" className="px-3">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Terms and Footer */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Términos y Pie de Página
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="payment_terms">Condiciones de Pago</Label>
                  <Textarea
                    id="payment_terms"
                    value={settings.payment_terms}
                    onChange={(e) => handleInputChange('payment_terms', e.target.value)}
                    placeholder="50% de adelanto, 50% contra entrega..."
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="footer_text">Texto de Pie de Página</Label>
                  <Textarea
                    id="footer_text"
                    value={settings.footer_text}
                    onChange={(e) => handleInputChange('footer_text', e.target.value)}
                    placeholder="Cotización válida por 30 días..."
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving} className="gap-2">
            <Save className="w-4 h-4" />
            {isSaving ? "Guardando..." : "Guardar Configuración"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompanySettingsManager;
